import { useEffect, useState } from "react";
import {
    Box,
    Flex,
    HStack,
    Text,
    Tag,
    Switch,
    Spinner,
    Center,
    useToast,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
} from "@chakra-ui/react";
import API from "../../helpers/axiosInstance";

// Turns the flat list into a tree keyed by parent_id, preserving API order.
// A page whose parent_id doesn't match anything in the list (deleted
// parent, bad data, etc.) is treated as top-level rather than silently
// dropped, so every page is guaranteed to show up somewhere.
const buildTree = (items, parentId = null) => {
    const ids = new Set(items.map((item) => item.id));
    return items
        .filter((item) => {
            const effectiveParent = ids.has(item.parent_id) ? item.parent_id : null;
            return effectiveParent === parentId;
        })
        .map((item) => ({ ...item, children: buildTree(items, item.id) }));
};

const countEnabled = (nodes) =>
    nodes.reduce(
        (acc, node) => {
            const child = countEnabled(node.children);
            return {
                enabled: acc.enabled + (node.permission ? 1 : 0) + child.enabled,
                total: acc.total + 1 + child.total,
            };
        },
        { enabled: 0, total: 0 }
    );

const PermissionSwitch = ({ node, onToggle }) => (
    <HStack spacing={3} flexShrink={0}>
        <Text
            fontSize="xs"
            fontWeight="500"
            color={node.permission ? "green.600" : "gray.400"}
            minW="58px"
            textAlign="right"
        >
            {node.permission ? "Enabled" : "Disabled"}
        </Text>
        <Switch
            colorScheme="blue"
            isChecked={!!node.permission}
            onChange={() => onToggle(node.id, node.permission)}
        />
    </HStack>
);

// A page with no sub-pages: a plain row with its own toggle.
const LeafRow = ({ node, depth, onToggle }) => (
    <AccordionItem border="none" borderBottom="1px solid" borderColor="gray.100">
        <Flex
            align="center"
            justify="space-between"
            gap={3}
            py={2.5}
            pl={4 + depth * 6}
            pr={4}
            _hover={{ bg: "gray.50" }}
        >
            <Box minW={0}>
                <Text fontSize="sm" fontWeight={depth === 0 ? "600" : "400"} color="gray.800" noOfLines={1}>
                    {node.name}
                </Text>
                <Text fontSize="xs" fontFamily="mono" color="gray.500" noOfLines={1}>
                    {node.slug}
                </Text>
            </Box>
            <PermissionSwitch node={node} onToggle={onToggle} />
        </Flex>
    </AccordionItem>
);

// A page with sub-pages: an expandable row (name, slug, enabled/total count)
// that reveals its children, same pattern as the admin Pages Management
// screen — a page can nest more than one level (e.g. Settings -> User
// Management -> Roles and Permission), so this recurses into itself.
const BranchRow = ({ node, depth, onToggle }) => {
    const { enabled, total } = countEnabled(node.children);
    const allOn = total > 0 && enabled === total;

    return (
        <AccordionItem border="none" borderBottom="1px solid" borderColor="gray.100">
            <HStack spacing={0} _hover={{ bg: "gray.50" }}>
                <AccordionButton py={3} pl={4 + depth * 6} pr={2} _hover={{ bg: "transparent" }}>
                    <HStack flex="1" spacing={3} minW={0} textAlign="left">
                        <Text
                            fontSize="sm"
                            fontWeight={depth === 0 ? "600" : "500"}
                            color="gray.800"
                            noOfLines={1}
                        >
                            {node.name}
                        </Text>
                        <Text fontSize="xs" fontFamily="mono" color="gray.500" noOfLines={1}>
                            {node.slug}
                        </Text>
                        <Tag
                            size="sm"
                            variant="subtle"
                            colorScheme={allOn ? "green" : enabled ? "blue" : "gray"}
                            flexShrink={0}
                        >
                            {enabled}/{total} on
                        </Tag>
                    </HStack>
                    <AccordionIcon color="gray.400" />
                </AccordionButton>
                <Box pr={3} flexShrink={0}>
                    <PermissionSwitch node={node} onToggle={onToggle} />
                </Box>
            </HStack>
            <AccordionPanel p={0} bg="gray.50">
                <Accordion allowMultiple>
                    {node.children.map((child) => (
                        <PageNode key={child.id} node={child} depth={depth + 1} onToggle={onToggle} />
                    ))}
                </Accordion>
            </AccordionPanel>
        </AccordionItem>
    );
};

const PageNode = ({ node, depth, onToggle }) =>
    node.children.length === 0 ? (
        <LeafRow node={node} depth={depth} onToggle={onToggle} />
    ) : (
        <BranchRow node={node} depth={depth} onToggle={onToggle} />
    );

const PagePermission = ({ id }) => {
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(false);
    const toast = useToast();

    const getApiHeaders = () => ({
        "x-access-token": localStorage.getItem("user"),
    });

    // Fetch pages and permissions
    const fetchPages = async () => {
        setLoading(true);
        try {
            const res = await API.get(
                `${process.env.REACT_APP_API_URL}/pages/permisssion?customer_id=${id}`,
                { headers: getApiHeaders() }
            );
            setPages(res.data || []);
        } catch (err) {
            console.error(err);
            toast({
                title: "Failed to load pages",
                status: "error",
                duration: 3000,
            });
        } finally {
            setLoading(false);
        }
    };

    // Update permission
    const handleToggle = async (pageId, currentPermission) => {
        try {
            const status_id = currentPermission ? 0 : 1; // toggle
            await API.post(
                `${process.env.REACT_APP_API_URL}/pages/set-permission`,
                { page_id: pageId, status_id, customer_id: +id },
                { headers: getApiHeaders() }
            );

            // Update local state
            setPages((prev) =>
                prev.map((p) =>
                    p.id === pageId ? { ...p, permission: !currentPermission } : p
                )
            );

            toast({
                title: `Permission ${status_id ? "enabled" : "disabled"}`,
                status: "success",
                duration: 2000,
            });
        } catch (err) {
            console.error(err);
            toast({
                title: "Failed to update permission",
                status: "error",
                duration: 3000,
            });
        }
    };

    useEffect(() => {
        fetchPages();
    }, [id]);

    if (loading) {
        return (
            <Center py={12}>
                <Spinner size="lg" color="blue.500" thickness="3px" />
            </Center>
        );
    }

    const tree = buildTree(pages);
    const summary = countEnabled(tree);

    if (tree.length === 0) {
        return (
            <Center py={12}>
                <Text color="gray.500" fontSize="sm">No pages available for this customer.</Text>
            </Center>
        );
    }

    return (
        <Box>
            <Flex justify="space-between" align="center" mb={4}>
                <Box>
                    <Text fontSize="md" fontWeight="600" color="gray.800">Page Access</Text>
                    <Text fontSize="xs" color="gray.500">
                        Choose which sections this customer can see in the portal.
                    </Text>
                </Box>
                <Tag size="md" variant="subtle" colorScheme="blue">
                    {summary.enabled} of {summary.total} enabled
                </Tag>
            </Flex>

            <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden" bg="white">
                <Accordion allowMultiple>
                    {tree.map((node) => (
                        <PageNode key={node.id} node={node} depth={0} onToggle={handleToggle} />
                    ))}
                </Accordion>
            </Box>
        </Box>
    );
};

export default PagePermission;
